import React from 'react';
import PropTypes from 'prop-types';

import {Button, Box, Stack, Typography} from 'views/components';

import {Translate, translate} from 'views/module';

import NotificationStatusTag from './NotificationStatusTag';
import SubscribeUserDialog from '../dialogs/SubscribeUserDialog';
import ConfirmDialog from '../dialogs/ConfirmDialog';

const EmailNotification = (props) => {
  const {clientBackend, relationshipData, userNotificationChannels} = props;
  const [isLoading, setIsLoading] = React.useState(false);
  const [showDialog, setShowDialog] = React.useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = React.useState(false);
  const [confirmAction, setConfirmAction] = React.useState(null);
  const [confirmDialogProps, setConfirmDialogProps] = React.useState({});
  const [email, setEmail] = React.useState(userNotificationChannels.email ?? '');

  React.useEffect(() => {
    setEmail(userNotificationChannels.email ?? '');
  }, [userNotificationChannels.email]);

  const createUserEmail = async (email) => {
    setIsLoading(true);
    const result = await clientBackend.notification.createUserEmail(relationshipData.relationshipRn, email, () => setIsLoading(false));
    if (!result) {
      setEmail('');
    }
  };

  const removeUserEmail = async () => {
    setIsLoading(true);
    await clientBackend.notification.removeUserEmail(relationshipData.relationshipRn, email, () => setIsLoading(false));
  };

  const handleOpenDialog = () => {
    const emailContact = relationshipData.contacts.find((contact) => contact.type === 'Email');
    const existingEmail = emailContact ? emailContact.detail.trim() : '';
    setEmail(existingEmail);
    setShowDialog(true);
  };

  const handleConfirm = async () => {
    if (confirmAction) {
      await confirmAction();
    }
    setShowConfirmDialog(false);
  };

  const getButtonConfig = () => {
    const channelTypeTranslated = translate('client.ClientDetail.Relationships.Notification.ChannelType.Email');
    if (userNotificationChannels.email) {
      return {
        text: translate('client.ClientDetail.Relationships.Notification.Unsubscribe', {channelType: channelTypeTranslated}),
        action: () => {
          setConfirmDialogProps({
            title: translate('client.ClientDetail.Relationships.Notification.Dialogs.ConfirmUnsubscribeTitle'),
            message: translate('client.ClientDetail.Relationships.Notification.Dialogs.ConfirmUnsubscribeMessage'),
            confirmText: translate('client.ClientDetail.Relationships.Notification.Unsubscribe', {channelType: channelTypeTranslated}),
          });
          setConfirmAction(() => removeUserEmail);
          setShowConfirmDialog(true);
        },
      };
    } else {
      return {
        text: translate('client.ClientDetail.Relationships.Notification.Subscribe', {channelType: channelTypeTranslated}),
        action: handleOpenDialog,
      };
    }
  };

  const buttonConfig = getButtonConfig();

  return (
    <Stack>
      <Typography
        variant="overline"
        sx={{
          fontWeight: 500,
        }}
      >
        <Translate expression='client.ClientDetail.Relationships.Notification.ChannelType.Email'/>
      </Typography>
      <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%'}}>
        <Box>
          <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.5}}>
            <Box sx={{fontWeight: 500, fontSize: '14px'}}>{email}</Box>
            <Box sx={{display: 'flex', alignItems: 'center'}}>
              <NotificationStatusTag userNotificationChannel={userNotificationChannels.email} />
            </Box>
          </Box>
        </Box>
        <Box sx={{display: 'flex', gap: 2}}>
          <Button
            onClick={buttonConfig.action}
            variant="contained"
            color="primary"
            disabled={isLoading}
          >
            {buttonConfig.text}
          </Button>
        </Box>
      </Box>
      {
        showDialog &&
        <SubscribeUserDialog
          open={showDialog}
          onClose={() => {
            setEmail('');
            setShowDialog(false);
          }}
          onSubmit={(email) => {
            createUserEmail(email);
            setShowDialog(false);
          }}
          initialChannelValue={email}
          isLoading={isLoading}
          channelType={'Email'}
        />
      }
      {
        showConfirmDialog &&
        <ConfirmDialog
          open={showConfirmDialog}
          title={confirmDialogProps.title}
          message={confirmDialogProps.message}
          onCancel={() => setShowConfirmDialog(false)}
          onConfirm={handleConfirm}
          confirmText={confirmDialogProps.confirmText}
          cancelText={translate('client.ClientDetail.Relationships.Notification.Cancel')}
          isLoading={isLoading}
        />
      }
    </Stack>
  );
};

EmailNotification.propTypes = {
  clientBackend: PropTypes.object.isRequired,
  userNotificationChannels: PropTypes.shape({
    email: PropTypes.string,
    smsNumber: PropTypes.string,
  }).isRequired,
  relationshipData: PropTypes.object.isRequired,
};

export default EmailNotification;
